/// CreateInstanceDialog: Modal dialog for creating a process instance
/// with initial variables in JSON format. Validates JSON before submission.

import { useState, useCallback } from "react";
import { Play, X } from "lucide-react";
import { useInstances } from "@/hooks/useInstances";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CreateInstanceDialogProps {
  /** The process ID to create an instance for. */
  processId: string;
  /** The display name of the process. */
  processName: string;
  /** Whether BPMN or node flow. */
  engineType: "bpmn" | "nodeflow";
  /** Whether the dialog is open. */
  open: boolean;
  /** Callback to change dialog open state. */
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreateInstanceDialog({
  processId,
  processName,
  engineType,
  open,
  onOpenChange,
}: CreateInstanceDialogProps) {
  const [variablesJson, setVariablesJson] = useState("{}");
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const { startInstance, startNodeFlowInstance } = useInstances();

  const handleStart = useCallback(async () => {
    // Validate JSON
    let variables: Record<string, unknown>;
    try {
      const parsed = JSON.parse(variablesJson);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        setError('Variables must be a JSON object (e.g. {"key": "value"})');
        return;
      }
      variables = parsed;
    } catch {
      setError("Invalid JSON syntax");
      return;
    }

    setIsStarting(true);
    setError(null);

    try {
      if (engineType === "bpmn") {
        await startInstance(processId, variables);
      } else {
        await startNodeFlowInstance(processId, variables);
      }
      // Reset and close
      setVariablesJson("{}");
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start instance");
    } finally {
      setIsStarting(false);
    }
  }, [variablesJson, processId, engineType, startInstance, startNodeFlowInstance, onOpenChange]);

  const handleClose = useCallback(() => {
    setError(null);
    setVariablesJson("{}");
    onOpenChange(false);
  }, [onOpenChange]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-md rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-700 px-4 py-3">
          <h2 className="text-sm font-semibold text-zinc-200">Create Process Instance</h2>
          <button
            onClick={handleClose}
            className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4">
          <p className="text-xs text-zinc-400 mb-4">
            Start a new instance of <span className="font-medium text-zinc-300">{processName}</span>{" "}
            <span className="text-zinc-500">({processId})</span>
          </p>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-zinc-400">
              Initial Variables (JSON)
            </label>
            <textarea
              value={variablesJson}
              onChange={(e) => {
                setVariablesJson(e.target.value);
                setError(null);
              }}
              className={[
                "w-full h-32 rounded border bg-zinc-800 p-3 font-mono text-sm text-zinc-200",
                "placeholder:text-zinc-600",
                "focus:outline-none focus:ring-1",
                error
                  ? "border-red-500/50 focus:ring-red-500/50"
                  : "border-zinc-700 focus:ring-blue-500/50",
              ].join(" ")}
              placeholder='{"key": "value"}'
              spellCheck={false}
            />
            {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-zinc-700 px-4 py-3">
          <button
            onClick={handleClose}
            className="rounded px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            disabled={isStarting}
            className={[
              "flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors",
              isStarting
                ? "bg-zinc-700 text-zinc-400 cursor-wait"
                : "bg-green-600 text-white hover:bg-green-500",
            ].join(" ")}
          >
            <Play size={14} />
            {isStarting ? "Starting..." : "Start Instance"}
          </button>
        </div>
      </div>
    </div>
  );
}
